// @givmo/compliance — the signer page's parts (CCM-R1-SIGN-WEB; slice 2 §6(b), frames 38, 38-390
// and 40): the reads a request needs, its header, the page
// column with the typed name drawn at the form's own signature place, and what goes with the form.
//
// ══ THE PAGE IS THE REQUEST'S OWN RENDITION SET ═══════════════════════════════════════════════
//
// The pages shown are the request's (`documents[].pages`): the set the act echoes as shown, so the
// signing record can say the signer was shown exactly these pages. They are never re-drawn here, and
// never the workbench's live preview. The typed name is laid over the image in an SVG whose view box
// is the page's crop box in PDF points, so the place the form declares lands exactly where the
// server will draw the signature, at any width the image is shown. It is a preview: the signed form
// is composed by the server, in the same font file.
//
// ══ EVERY DOCUMENT OF THE PACKAGE ══════════════════════════════════════════════════════════════
//
// One Sign signs every place the caller holds, on every document of the package, and the signing
// record says the signer was shown all of their pages. So "Show all pages" shows EVERY document's
// pages: where the package holds more than one, each page is captioned with its document's served
// form number ("Annual Update of Registration Form · Page 2 of 4"), and each document has its own
// "Download this PDF". A package of one document reads exactly as frames 38 and 40 draw it.
//
// ══ AFTER THE LAST SIGNATURE ═══════════════════════════════════════════════════════════════════
//
// The column shows each signed form's own pages ("…, as signed"): a final's drawn pages, found
// through its filing document's versions (the `signed_final` made from the version signed). A final
// composed before those pages were stored answers 404 for them (dev only); that is no picture,
// never an error (cut 1002-7's review, P1).

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { FOCUS_RING, Icon, Skeleton, cn, testIdProp, toast } from '@givmo/design-system';
import { FileText } from 'lucide-react';
import { isGivmoError } from '@givmo/api-client';
import { useCompliance } from './compliance-context';
import {
  decodeChecklist,
  decodeFilingCaseDetail,
  decodeVersionDownloadUrl,
  extractFormAdapters,
  type ChecklistItem,
  type DocumentVersion,
  type FilingCase,
  type FormAdapter,
} from './compliance-preparation-decode';
import {
  decodeFilingDocumentVersions,
  decodeSigningRequestDetail,
  decodeVersionRendition,
  signedFinalOf,
  type RenditionPage,
  type SigningDocument,
  type SigningPage,
  type SigningRequestDetail,
  type SigningSlot,
} from './compliance-signing-decode';
import {
  SIGNATURE_FONT_FAMILY,
  SIGNATURE_INK,
  askedLine,
  documentOf,
  firstPlace,
  longDateIn,
  moreNotNeededLine,
  myPlaces,
  noteLine,
  pageOfLine,
  senderName,
  showPageLine,
  typedNameLayout,
  DOWNLOAD_THIS_PDF,
  OPEN_FAILED,
  SHOW_ALL_PAGES,
  SIGNATURE_REQUESTED,
} from './compliance-signing-model';
import { NOT_ATTACHED_YET, WHAT_GOES_WITH_IT } from './compliance-preparation-model';
import {
  complianceProfileQueryKey,
  filingCaseQueryKey,
  filingChecklistQueryKey,
  filingDocumentQueryKey,
  filingOptionsQueryKey,
  formAdaptersQueryKey,
  listAllFilingOptions,
  listAllFormAdapters,
  obligationQueryKey,
  renewalCycleQueryKey,
  signingRequestQueryKey,
  versionRenditionQueryKey,
} from './compliance-queries';
import { QuietButton } from './filing-workbench-pages';
import { beforeFirstSolicitLine } from './compliance-panel-model';
import { decodeObligationDetail, decodeProfile, serverMessage } from './compliance-decode';
import { decodeRenewalCycleDetail } from './compliance-filings-decode';
import {
  lacksFiscalYearEnd,
  libraryNextDate,
  localIsoDate,
  ruleShapesOf,
} from './compliance-filings-model';
import { ruleShapesFromOptions } from './compliance-filings-reads';

const PREFIX = 'compliance-signing';

// ── The reads ──────────────────────────────────────────────────────────────────────────────────

export interface SigningReads {
  detail?: SigningRequestDetail;
  /** The request read failed with this. A 404 is "not found": this person isn't on it, or it's gone. */
  failed?: unknown;
  notFound: boolean;
  pending: boolean;
  /** "Maryland · Annual report", as Filings names the filing. */
  title?: string;
  /** "Annual Update of Registration Form · fiscal year ended June 30, 2026". */
  subtitle?: string;
  jurisdiction: string;
  checklist: ChecklistItem[];
  sender?: string;
  /** A document's served form number ("Annual Update of Registration Form"), by its adapter. */
  formNumberOf: (adapterKey: string) => string | undefined;
}

/** One request, and what its page needs around it: the filing's names and its checklist. */
export function useSigningReads(requestId: string): SigningReads {
  const { orgRef, isReady, operations, moduleStanding, personName } = useCompliance();
  const ref = isReady && moduleStanding === 'available' ? orgRef : null;
  const read = useQuery({
    queryKey: signingRequestQueryKey(ref, requestId),
    queryFn: () => operations.getSigningRequest(ref ?? '', requestId),
    enabled: ref !== null && requestId.length > 0,
    retry: (count, error) => !(isGivmoError(error) && error.status === 404) && count < 1,
  });
  const detail = decodeSigningRequestDetail(read.data);
  const caseId = detail?.request.filingCaseId;
  const caseRead = useQuery({
    queryKey: filingCaseQueryKey(ref, caseId),
    queryFn: () => operations.getFilingCase(ref ?? '', caseId ?? ''),
    enabled: ref !== null && caseId !== undefined,
  });
  // The case's own read answers `{ case, documents }`, never the bare case the POST answers.
  const filingCase: FilingCase | undefined = decodeFilingCaseDetail(caseRead.data);
  const adapters = useQuery({
    queryKey: formAdaptersQueryKey(ref),
    queryFn: () => listAllFormAdapters(operations, ref ?? ''),
    enabled: ref !== null && detail !== undefined,
  });
  const checklist = useQuery({
    queryKey: filingChecklistQueryKey(ref, caseId),
    queryFn: () => operations.getFilingCaseChecklist(ref ?? '', caseId ?? ''),
    enabled: ref !== null && caseId !== undefined,
  });
  // The filing's names come from the case's own filing and year, never the organization's whole
  // Filings composition (P4): the filing with its dates, and the year the case is for.
  const obligationId = filingCase?.obligationId;
  const obligationRead = useQuery({
    queryKey: obligationQueryKey(ref, obligationId),
    queryFn: () => operations.getObligation(ref ?? '', obligationId ?? ''),
    enabled: ref !== null && obligationId !== undefined,
  });
  const cycleId = filingCase?.renewalCycleId;
  const cycleRead = useQuery({
    queryKey: renewalCycleQueryKey(ref, cycleId),
    queryFn: () => operations.getRenewalCycle(ref ?? '', cycleId ?? ''),
    enabled: ref !== null && cycleId !== undefined,
  });
  const filing = decodeObligationDetail(obligationRead.data);
  const obligation = filing.obligation;
  const jurisdiction = obligation?.jurisdictionName || obligation?.jurisdictionCode || '';
  const formAdapters = extractFormAdapters(adapters.data);
  const formNumberOf = (adapterKey: string) =>
    formAdapters.find((candidate) => candidate.adapterKey === adapterKey)?.formNumber;
  const adapter: FormAdapter | undefined = formAdapters.find(
    (candidate) => candidate.adapterKey === detail?.documents[0]?.adapterKey,
  );
  const cycle = decodeRenewalCycleDetail(cycleRead.data);
  const periodEnd = cycle?.periodEnd;
  // A filing made once (its case names no period): when it is due, by the rule the workbench's send
  // sheet names it with (`libraryNextDate`; CCM-R1-SIGN-WEB-WITHDRAWN, cut 1003-5's review F2). Its
  // own dates, and, with no fiscal year end recorded (the register then dates nothing and serves no
  // occurrence), its rule's deadline shapes from the filing options, read only then: the Filings
  // composition's own reads, under their keys. A read that fails answers nothing, never a guess.
  const once = filingCase !== undefined && filingCase.renewalCycleId === undefined;
  const profileWanted = ref !== null && once && obligation !== undefined;
  const profileRead = useQuery({
    queryKey: complianceProfileQueryKey(ref),
    queryFn: () => operations.getProfile(ref ?? ''),
    enabled: profileWanted,
  });
  const profile = decodeProfile(profileRead.data);
  const optionsWanted = profileWanted && lacksFiscalYearEnd(profile);
  const optionsRead = useQuery({
    queryKey: filingOptionsQueryKey(ref),
    queryFn: () => listAllFilingOptions(operations, ref ?? ''),
    enabled: optionsWanted,
  });
  const next =
    once && obligation !== undefined
      ? libraryNextDate(
          obligation,
          [],
          filing.occurrences,
          profile,
          localIsoDate(new Date()),
          optionsWanted && optionsRead.data !== undefined
            ? ruleShapesOf(ruleShapesFromOptions(optionsRead.data), obligation)
            : undefined,
        )
      : undefined;
  const firstSolicit = next?.kind === 'before-first-solicitation';
  const when =
    periodEnd !== undefined && cycle !== undefined && !cycle.cycleKey.startsWith('standing')
      ? `fiscal year ended ${longDateIn(periodEnd)}`
      : firstSolicit && jurisdiction.length > 0
        ? beforeFirstSolicitLine(jurisdiction).replace(/^B/, 'b')
        : undefined;
  const formName = adapter?.formNumber;
  const subtitle =
    formName !== undefined ? (when !== undefined ? `${formName} · ${when}` : formName) : undefined;
  // As Filings names the filing: its jurisdiction, then its title.
  const title =
    obligation !== undefined && jurisdiction.length > 0
      ? obligation.displayTitle !== undefined
        ? `${jurisdiction} · ${obligation.displayTitle}`
        : jurisdiction
      : undefined;
  const failed = read.error ?? undefined;
  return {
    detail,
    failed,
    notFound: isGivmoError(failed) && failed.status === 404,
    // A read not yet made (no organization yet) is pending too: never a flash of "not found". And
    // the page waits for the filing's names and checklist with it, so nothing renders twice (no
    // "the agency" before "Maryland"); a read that fails stops waiting.
    pending:
      read.isPending ||
      (detail !== undefined &&
        (caseRead.isPending ||
          (obligationId !== undefined && obligationRead.isPending) ||
          (cycleId !== undefined && cycleRead.isPending) ||
          (profileWanted && profileRead.isPending) ||
          (optionsWanted && optionsRead.isPending) ||
          adapters.isPending ||
          checklist.isPending)),
    ...(title !== undefined ? { title } : {}),
    ...(subtitle !== undefined ? { subtitle } : {}),
    jurisdiction,
    checklist: decodeChecklist(checklist.data),
    ...(detail !== undefined && senderName(detail, personName) !== undefined
      ? { sender: senderName(detail, personName) }
      : {}),
    formNumberOf,
  };
}

// ── The header (frames 38, 40) ──────────────────────────────────────────────────────────────────

export function SigningHeader({
  reads,
  showSignBy,
}: {
  reads: SigningReads;
  /** Frame 40 drops "· Sign by …" once the person has signed. */
  showSignBy: boolean;
}) {
  const detail = reads.detail;
  if (detail === undefined) return null;
  const zone = detail.version.timeZone;
  const message = detail.version.message?.trim();
  // Frame 38's two blocks, as its source sets them: the title block, then (8 px closer than the
  // page's gap) who asked and their note.
  return (
    <header className="flex flex-col gap-16" {...testIdProp(`${PREFIX}-header`)}>
      <div className="flex flex-col gap-4">
        <p className="text-s12 text-text-secondary">{SIGNATURE_REQUESTED}</p>
        {reads.title !== undefined && (
          <h1 className="font-sans text-s22 font-w700 tracking-givmo text-text-strong">
            {reads.title}
          </h1>
        )}
        {reads.subtitle !== undefined && (
          <p
            className="font-sans text-s14 tracking-givmo text-text-secondary"
            {...testIdProp(`${PREFIX}-subtitle`)}
          >
            {reads.subtitle}
          </p>
        )}
      </div>
      <div className="flex flex-col gap-10">
        <p className="text-s13 text-text" {...testIdProp(`${PREFIX}-asked`)}>
          {askedLine(
            reads.sender,
            detail.version.issuedAt ?? detail.request.createdAt,
            showSignBy ? detail.request.expiresAt : undefined,
            zone,
          )}
        </p>
        {message !== undefined && message.length > 0 && (
          // A note keeps its line breaks (D-CCM-35 item 5 (C)).
          <blockquote
            className="max-w-[720px] whitespace-pre-line border-l-2 border-border-strong pl-12 text-s13 leading-h15 text-text-secondary"
            {...testIdProp(`${PREFIX}-note`)}
          >
            {noteLine(reads.sender, message)}
          </blockquote>
        )}
      </div>
    </header>
  );
}

// ── The signature font ─────────────────────────────────────────────────────────────────────────

/**
 * Whether the signature font has loaded: the previews wait for it (`font-display: block` keeps a
 * fallback face from ever standing in for a signature). Where the browser has no font loading API
 * (a test environment), it is ready at once.
 */
export function useSignatureFontReady(): boolean {
  const [ready, setReady] = useState(
    () => typeof document === 'undefined' || document.fonts === undefined,
  );
  useEffect(() => {
    if (ready) return;
    let cancelled = false;
    void document.fonts
      .load(`20px "${SIGNATURE_FONT_FAMILY}"`)
      .catch(() => undefined)
      .then(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [ready]);
  return ready;
}

/** A typed name's width at 1 pt in the signature font, from the loaded face (0 before it loads). */
export function useNameWidthAtOnePoint(name: string, fontReady: boolean): number {
  return useMemo(() => {
    if (!fontReady || name.length === 0 || typeof document === 'undefined') return 0;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext?.('2d');
    if (context === null || context === undefined) return 0;
    context.font = `100px "${SIGNATURE_FONT_FAMILY}"`;
    const width = context.measureText(name).width;
    return Number.isFinite(width) && width > 0 ? width / 100 : 0;
  }, [name, fontReady]);
}

/** "Daniel Okonkwo" in the signature font, on a line: the panel's "How it looks on page 4". */
export function TypedNamePreview({ name, fontReady }: { name: string; fontReady: boolean }) {
  return (
    <div
      className="flex h-[52px] items-end overflow-hidden border-b border-border-strong px-4 pb-4"
      {...testIdProp(`${PREFIX}-preview`)}
    >
      {fontReady && name.length > 0 && (
        <span
          className="truncate text-[30px] leading-none"
          style={{ fontFamily: `"${SIGNATURE_FONT_FAMILY}"`, color: SIGNATURE_INK }}
          {...testIdProp(`${PREFIX}-preview-name`)}
        >
          {name}
        </span>
      )}
    </div>
  );
}

// ── The page column ────────────────────────────────────────────────────────────────────────────

/** The typed name over one page, at each of the caller's places on it. */
function TypedNameOverlay({
  page,
  places,
  name,
  widthAtOnePoint,
}: {
  page: SigningPage;
  places: readonly SigningSlot[];
  name: string;
  widthAtOnePoint: number;
}) {
  const layouts = places
    .map((slot) => typedNameLayout(slot.rect, page, widthAtOnePoint))
    .filter((layout): layout is NonNullable<typeof layout> => layout !== undefined);
  const first = layouts[0];
  if (first === undefined) return null;
  const drawn = name.length > 0 && widthAtOnePoint > 0;
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox={`0 0 ${first.viewWidth} ${first.viewHeight}`}
      preserveAspectRatio="none"
      aria-hidden
      {...testIdProp(`${PREFIX}-overlay-${page.page}`)}
    >
      {layouts.map((layout, index) => (
        <g key={index}>
          {/* The place itself, outlined as frame 38 outlines it. */}
          <rect
            x={layout.place.x}
            y={layout.place.y}
            width={layout.place.width}
            height={layout.place.height}
            rx={3}
            className="fill-brand-500/[0.06] stroke-brand-500/40"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
            {...testIdProp(`${PREFIX}-overlay-place`)}
          />
          {drawn && (
            <>
              <clipPath id={`${PREFIX}-clip-${page.page}-${index}`}>
                <rect
                  x={layout.place.x}
                  y={layout.place.y}
                  width={layout.place.width}
                  height={layout.place.height}
                />
              </clipPath>
              <text
                x={layout.x}
                y={layout.baseline}
                fontSize={layout.fontSize}
                fill={SIGNATURE_INK}
                style={{ fontFamily: `"${SIGNATURE_FONT_FAMILY}"` }}
                clipPath={`url(#${PREFIX}-clip-${page.page}-${index})`}
                {...testIdProp(`${PREFIX}-overlay-name`)}
              >
                {name}
              </text>
            </>
          )}
        </g>
      ))}
    </svg>
  );
}

/** One page image, at its own aspect ratio, with whatever is drawn over it. */
function PageImage({
  url,
  width,
  height,
  page,
  children,
}: {
  url: string;
  width: number;
  height: number;
  page: number;
  children?: ReactNode;
}) {
  return (
    <div
      className="relative w-full overflow-hidden rounded-lg border border-border-subtle bg-white shadow-xs"
      style={{ aspectRatio: `${width} / ${height}` }}
      {...testIdProp(`${PREFIX}-page-${page}`)}
    >
      <img
        src={url}
        alt=""
        width={width}
        height={height}
        className="block h-full w-full select-none"
        draggable={false}
      />
      {children}
    </div>
  );
}

/** Open a version's exact bytes (DOCS-A's logged link). */
export function useOpenVersion() {
  const { orgRef, operations } = useCompliance();
  return async (documentId: string, versionId: string) => {
    try {
      const url = decodeVersionDownloadUrl(
        await operations.getDocumentVersionDownload(orgRef ?? '', documentId, versionId),
      );
      if (url === undefined) throw new Error(OPEN_FAILED);
      window.location.assign(url);
    } catch (cause) {
      toast.error(serverMessage(cause, OPEN_FAILED));
    }
  };
}

/** One document's signed final, and its drawn pages, once the request completes. */
export interface SignedFinal {
  documentId: string;
  final?: DocumentVersion;
  /** The final's drawn pages: none for a final composed before they were kept (404, P1). */
  pages: RenditionPage[];
  /** Which final it is, or its pages, is still being read: not yet "no picture". */
  pagesPending: boolean;
}

/** Every document's signed final and its drawn pages, in the package's order, once the request completes. */
export function useSignedFinals(detail: SigningRequestDetail | undefined): SignedFinal[] {
  const { orgRef, isReady, operations, moduleStanding } = useCompliance();
  const ref = isReady && moduleStanding === 'available' ? orgRef : null;
  const completed = detail?.request.state === 'completed';
  const documents = detail?.documents ?? [];
  const versions = useQueries({
    queries: documents.map((document) => ({
      queryKey: filingDocumentQueryKey(ref, document.documentId),
      queryFn: () => operations.getFilingDocument(ref ?? '', document.documentId),
      enabled: ref !== null && completed,
    })),
  });
  const finals = documents.map((document, index) =>
    signedFinalOf(decodeFilingDocumentVersions(versions[index]?.data), document.documentVersionId),
  );
  const renditions = useQueries({
    queries: documents.map((document, index) => ({
      queryKey: versionRenditionQueryKey(ref, document.documentId, finals[index]?.versionId),
      queryFn: () =>
        operations.getDocumentVersionRendition(
          ref ?? '',
          document.documentId,
          finals[index]?.versionId ?? '',
        ),
      enabled: ref !== null && finals[index] !== undefined,
      retry: (count: number, error: unknown) =>
        !(isGivmoError(error) && error.status === 404) && count < 1,
    })),
  });
  return documents.map((document, index) => ({
    documentId: document.documentId,
    ...(finals[index] !== undefined ? { final: finals[index] } : {}),
    // A final composed before its pages were kept answers 404: no picture, never an error (P1).
    pages: decodeVersionRendition(renditions[index]?.data),
    pagesPending:
      finals[index] !== undefined
        ? renditions[index]?.isPending === true
        : versions[index]?.isPending === true,
  }));
}

/**
 * The left column (frames 38, 40): the page holding the caller's place, the way to every page
 * and to the exact PDF, and the typed name drawn at the place. Once the request completes, the
 * signed form's own pages, "as signed". Where the package holds several documents, "Show all
 * pages" shows every document's pages, each captioned with its document's form number, each
 * document with its own download.
 */
export function SigningPageColumn({
  detail,
  typedName,
  fontReady,
  widthAtOnePoint,
  drawName,
  formNumberOf,
}: {
  detail: SigningRequestDetail;
  typedName: string;
  fontReady: boolean;
  widthAtOnePoint: number;
  /** Whether the typed name is drawn at the caller's place (not once they have signed). */
  drawName: boolean;
  /** A document's served form number, by its adapter (the reads' `formNumberOf`). */
  formNumberOf?: (adapterKey: string) => string | undefined;
}) {
  const place = firstPlace(detail);
  const document = documentOf(detail, place);
  const placePage = place?.page ?? document?.pages[0]?.page ?? 1;
  const [allPages, setAllPages] = useState(false);
  const finals = useSignedFinals(detail);
  const openVersion = useOpenVersion();
  const completed = detail.request.state === 'completed';
  const mine = myPlaces(detail);
  const several = detail.documents.length > 1;

  /** A document as the column shows it: its pages (as signed, once its final is drawn). */
  const viewOf = (shown: SigningDocument) => {
    const signed = finals.find((entry) => entry.documentId === shown.documentId);
    const asSigned = completed && signed?.final !== undefined;
    return {
      signed,
      asSigned,
      count: asSigned ? (signed?.final?.pageCount ?? shown.pageCount) : shown.pageCount,
      numbers: asSigned
        ? (signed?.pages ?? []).map((page) => page.page)
        : shown.pages.map((page) => page.page),
      versionId:
        asSigned && signed?.final !== undefined ? signed.final.versionId : shown.documentVersionId,
    };
  };

  /** "Page 2 of 4", with its document's form number before it where the package holds several. */
  const caption = (shown: SigningDocument, page: number, count: number, asSigned: boolean) => {
    const line = pageOfLine(page, count, asSigned);
    const form = several ? formNumberOf?.(shown.adapterKey) : undefined;
    return form !== undefined ? `${form} · ${line}` : line;
  };

  const renderPage = (shown: SigningDocument, number: number, view: ReturnType<typeof viewOf>) => {
    const key = `${shown.documentId}-${number}`;
    if (view.asSigned) {
      const page = view.signed?.pages.find((candidate) => candidate.page === number);
      return page === undefined ? null : (
        <PageImage
          key={key}
          url={page.imageUrl}
          width={page.width}
          height={page.height}
          page={number}
        />
      );
    }
    const page = shown.pages.find((candidate) => candidate.page === number);
    if (page === undefined) return <Skeleton key={key} className="h-[480px] w-full" />;
    const placesHere = mine.filter(
      (slot) => slot.documentId === shown.documentId && slot.page === number,
    );
    return (
      <PageImage
        key={key}
        url={page.imageUrl}
        width={page.width}
        height={page.height}
        page={number}
      >
        {drawName && fontReady && (
          <TypedNameOverlay
            page={page}
            places={placesHere}
            name={typedName}
            widthAtOnePoint={widthAtOnePoint}
          />
        )}
      </PageImage>
    );
  };

  const first = document !== undefined ? viewOf(document) : undefined;
  const everyDocument = several && allPages;

  return (
    <section className="flex min-w-0 flex-col gap-12" {...testIdProp(`${PREFIX}-pages`)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-4">
        {!everyDocument && (
          <h2
            className="text-s14 font-w600 text-text-strong"
            {...testIdProp(`${PREFIX}-page-heading`)}
          >
            {document !== undefined && first !== undefined
              ? caption(
                  document,
                  allPages ? (first.numbers[0] ?? placePage) : placePage,
                  first.count,
                  first.asSigned,
                )
              : pageOfLine(placePage, 0)}
          </h2>
        )}
        <span className="flex flex-wrap gap-16">
          <QuietButton
            onClick={() => setAllPages((value) => !value)}
            testId={`${PREFIX}-all-pages`}
          >
            {allPages ? showPageLine(placePage) : SHOW_ALL_PAGES}
          </QuietButton>
          {document !== undefined && first !== undefined && !everyDocument && (
            <QuietButton
              onClick={() => void openVersion(document.documentId, first.versionId)}
              testId={`${PREFIX}-download`}
            >
              {DOWNLOAD_THIS_PDF}
            </QuietButton>
          )}
        </span>
      </div>
      {everyDocument
        ? detail.documents.map((shown) => {
            const view = viewOf(shown);
            return (
              <div
                key={shown.documentId}
                className="flex min-w-0 flex-col gap-12"
                {...testIdProp(`${PREFIX}-document`)}
              >
                {view.numbers.map((number, index) => (
                  <div key={number} className="flex min-w-0 flex-col gap-8">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-4">
                      <p
                        className="text-s14 font-w600 text-text-strong"
                        {...testIdProp(`${PREFIX}-page-caption`)}
                      >
                        {caption(shown, number, view.count, view.asSigned)}
                      </p>
                      {index === 0 && (
                        <QuietButton
                          onClick={() => void openVersion(shown.documentId, view.versionId)}
                          testId={`${PREFIX}-download-document`}
                        >
                          {DOWNLOAD_THIS_PDF}
                        </QuietButton>
                      )}
                    </div>
                    {renderPage(shown, number, view)}
                  </div>
                ))}
              </div>
            );
          })
        : document !== undefined && first !== undefined
          ? (allPages ? first.numbers : [placePage]).map((number) =>
              renderPage(document, number, first),
            )
          : null}
    </section>
  );
}

// ── What goes with it (read only) ──────────────────────────────────────────────────────────────

/** The checklist as the signer sees it: each attached file, each gap, and the rest not needed. */
export function SigningAttachments({ items }: { items: readonly ChecklistItem[] }) {
  const openVersion = useOpenVersion();
  if (items.length === 0) return null;
  const files = items.flatMap((item) =>
    item.status === 'attached' ? item.documents.map((document) => ({ item, document })) : [],
  );
  const gaps = items.filter((item) => item.status === 'open');
  const notNeeded = moreNotNeededLine(items.filter((item) => item.status === 'not_needed').length);
  return (
    <section className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-attachments`)}>
      <h2 className="text-s14 font-w600 text-text-strong">{WHAT_GOES_WITH_IT}</h2>
      <ul className="flex flex-col gap-8">
        {files.map(({ document }) => (
          <li key={document.documentId} className="flex min-w-0 items-center gap-10">
            <Icon icon={FileText} size={15} className="shrink-0 text-icon-muted" aria-hidden />
            {document.latestVersion !== undefined ? (
              // Frame 38's file row: the name itself, underlined, opens the filing's own copy.
              <button
                type="button"
                onClick={() =>
                  void openVersion(document.documentId, document.latestVersion?.versionId ?? '')
                }
                className={cn(
                  'min-w-0 truncate rounded-xs text-left text-s13 text-text underline decoration-border-strong underline-offset-2 hover:decoration-text-strong',
                  FOCUS_RING,
                )}
                {...testIdProp(`${PREFIX}-attachment`)}
              >
                {document.title}
              </button>
            ) : (
              <span className="min-w-0 truncate text-s13 text-text">{document.title}</span>
            )}
          </li>
        ))}
        {gaps.map((item) => (
          <li
            key={item.attachmentIndex}
            className="text-s13 text-text-secondary"
            {...testIdProp(`${PREFIX}-attachment-gap`)}
          >
            {`${item.item} · ${NOT_ATTACHED_YET}`}
          </li>
        ))}
      </ul>
      {notNeeded !== undefined && (
        <p
          className="text-s12 leading-h15 text-text-tertiary"
          {...testIdProp(`${PREFIX}-not-needed`)}
        >
          {notNeeded}
        </p>
      )}
    </section>
  );
}
